'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  CheckCircle,
  ForwardToInbox as ForwardToInboxIcon
} from '@mui/icons-material'
import axios from 'axios'
import { addDoc, collection, doc, getDoc } from 'firebase/firestore'
import { motion } from 'framer-motion'
import { get } from 'lodash'
import moment from 'moment'
import 'moment-timezone'
import Image from 'next/image' // For loading spinner
import { posthog } from 'posthog-js'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import ImageWithFallback from 'src/components/ImageWithFallBack'
import { db } from 'src/utils/firebase' // Assuming firebase is configured
import { getCountry, getLocation } from 'src/utils/location' // Assuming location utils exist
import * as yup from 'yup'

interface FormValues {
  email: string
}

const validationSchema = yup.object({
  email: yup.string().email('Email is invalid').required('Email is required')
})

const BenchmarkSection = () => {
  const [loading, setLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors, isValid, isDirty }
  } = useForm<FormValues>({
    defaultValues: {
      email: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'all'
  })

  async function onGetInsights(values: FormValues) {
    try {
      setLoading(true)
      const { email } = values

      // Assuming 'email_alerts' and 'inbound_subscribers' are the correct Firestore paths
      const emailsDoc = await getDoc(
        doc(db, 'email_alerts', 'inbound_subscribers')
      )
      const emails = get(emailsDoc.data(), 'emails', [])
      const location = await getLocation()
      const TriggerPage = 'Landing Page - Benchmark Section'
      const pageName = window.location.href
      const emailBody = {
        template_name: 'benchmark-signup', // Or a more relevant template
        to_emails: emails,
        extra_data: {
          email,
          source: TriggerPage,
          url: pageName,
          location: location,
          date_time: moment()
            .tz('America/Los_Angeles')
            .format('MMMM Do YYYY, h:mm:ss a z'),
          redirection_url: localStorage.getItem('referrer') ?? 'Direct'
        }
      }

      // Assuming 'landing-page-visitors' is the correct Firestore collection
      await addDoc(collection(db, 'landing-page-visitors'), {
        ...emailBody,
        created_at: new Date().toISOString()
      })

      const country = await getCountry()
      if (country !== 'India') {
        // Ensure this API endpoint is correct and active
        await axios.post(
          `https://api.loopapplication.xyz/notification/email_digest`,
          emailBody
        )
      }

      posthog.capture('Landing Leads', {
        email,
        TriggerPage,
        pageName
      })

      let user = {
        signup_date: Date.now() / 1000,
        email
      }
      posthog.identify(user.email, user)

      setEmailSent(true)
      reset()

      setTimeout(() => {
        setEmailSent(false)
      }, 5000)
    } catch (error) {
      console.error('Error submitting email:', error)
      // Potentially set an error state here to show to the user
    } finally {
      setLoading(false)
    }
  }

  // const resourceCards = [ // This seems unused, can be removed if not needed later
  //   {
  //     title: 'Blog & Press releases',
  //     description: 'Latest industry insights and company news',
  //     bgColor: 'bg-red-100'
  //   },
  //   {
  //     title: 'Case Studies',
  //     description: 'Real success stories from our clients',
  //     bgColor: 'bg-blue-100'
  //   },
  //   {
  //     title: 'Growth Playbooks',
  //     description: 'Strategic guides for delivery optimization',
  //     bgColor: 'bg-green-100'
  //   }
  // ]

  return (
    <motion.section
      data-item="benchmark-section"
      className="py-12 md:py-16 lg:py-24 bg-white relative z-10" // Removed duplicate 'relative'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}>
      <div className="container mx-auto px-4 md:px-8 text-center gap-4 flex flex-col z-10">
        <div className="items-center justify-center text-xs">
          Real data. Real operator stories.
        </div>
        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}>
          Unlock delivery benchmarks & <br className="hidden sm:block" />
          growth playbooks
        </motion.h2>

        <div className="text-sm sm:text-base md:text-lg text-gray-400 mt-0 mb-6 sm:mb-10">
          See how top brands improve ops, cut downtime, and boost delivery
          sales.
        </div>

        <form
          onSubmit={handleSubmit(onGetInsights)}
          className="flex justify-center mb-10 w-full">
          <div className="flex max-w-[280px] sm:max-w-md w-full flex-col sm:flex-row rounded-md overflow-hidden shadow-sm gap-2">
            <div className="w-full sm:flex-1  text-xs md:text-sm ">
              {' '}
              <input
                {...register('email')}
                type="email"
                placeholder="Type your work email"
                className={`w-full outline-none bg-white border transition-all focus:ring-1 focus:ring-gray-300 focus:bg-white  text-gray-600 rounded-lg shadow-lg px-4 py-3 md:py-4  ${
                  errors.email ? 'border-red-500' : 'border-gray-200'
                }`}
                aria-label="Work email"
              />
              {/* {errors.email && (
                <p className="text-red-500 text-xs mt-1 w-full text-left px-4">
                  {errors.email.message}
                </p>
              )} */}
            </div>
            <motion.button
              type="submit"
              disabled={!isDirty || !isValid || loading}
              className={`w-full sm:w-auto bg-[#1a2b42] text-white px-4 md:px-5 py-3 md:py-4 font-medium transition-all duration-200 text-xs md:text-sm rounded-lg whitespace-nowrap shadow-lg flex items-center justify-center h-[48px] md:h-[54px] ${
                // Fixed height for button consistency
                !isDirty || !isValid || loading
                  ? 'opacity-70 cursor-not-allowed'
                  : 'hover:bg-[#263b59] active:bg-[#132339]'
              }`}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}>
              {loading ? (
                <Image
                  src="/img/blogs/Loading.svg" // Ensure this path is correct or use a different spinner
                  alt="Loading..."
                  width={24} // Adjusted size
                  height={24}
                />
              ) : emailSent ? (
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}>
                  <CheckCircle sx={{ color: 'white' }} />
                </motion.div>
              ) : (
                <>
                  <ForwardToInboxIcon sx={{ fontSize: '18px', mr: 1 }} />
                  Get Insights
                </>
              )}
            </motion.button>
          </div>
        </form>

        <div
          data-item="flow-diagram"
          className="relative w-full h-[300px] sm:h-[350px] md:h-[400px]">
          <ImageWithFallback
            src="/v2/images/flow-diagram.svg"
            alt="Flow Diagram"
            fill
            className="object-contain"
          />
        </div>
      </div>

      <div
        className="absolute top-0 left-0 w-full h-full bg-repeat -z-10 opacity-50"
        style={{
          backgroundImage: 'url(/v2/images/dotted-pattern.svg)',
          backgroundPosition: 'center bottom'
        }}
      />
    </motion.section>
  )
}

export default BenchmarkSection
